<template>
  <div class="card" :class="{ 'card__payments': showPayments, 'card__progress': !isProjectApproved(project) }" v-if="project">
    <div class="card__image">
      <div class="card__image__inside">
        <div class="card__delivered" v-if="((project.delivered && !project.user_delivered) || (!project.delivered && !project.user_delivered && project.user_id == $store.state.userId)) && project.distribution_locked">
          <click-confirm button-size="sm" :messages="confirmMessage" v-if="!userLoading">
            <b-button v-b-tooltip.hover :title="$t('messages.project_delivered')" variant="primary" @click="userDelivered" class="mr-2">
              <font-awesome-icon icon="check"></font-awesome-icon>
            </b-button>
          </click-confirm>
          <b-button v-if="userLoading" variant="primary" :disabled="true">
            <font-awesome-icon icon="circle-notch" class="text-white" spin></font-awesome-icon>
          </b-button>
        </div>
        <a class="box-nav" :href="'/project/' + this.slugText(project.id + '/' + project.name)">
          <b-img :src="project.image" fluid :alt="project.name" />
        </a>
      </div>
    </div>
    <a class="box-nav max-height-40" :href="'/project/' + this.slugText(project.id + '/' + project.name)">
      <div class="card__info"
           :class="{ 'bg-success': isProjectApproved(project) && (isProjectOpen(project) || isProjectDelivered(project) || isProjectUserDelivered(project)),
                     'bg-danger': isProjectApproved(project) && isProjectFull(project) && !isProjectDelivered(project) && !isProjectUserDelivered(project),
                     'bg-warning': !isProjectApproved(project) || (!isProjectSet(project) && isProjectOpen(project) && !isProjectDelivered(project) && !isProjectUserDelivered(project)),
                     'bg-grayed': isProjectClosed(project) }">
        <div v-if="isProjectApproved(project)">
          <div v-if="isProjectStillJoinable(project) && !isProjectDelivered(project) && !isProjectUserDelivered(project)">
            {{ project.max_participants - project.current_participants }} {{ (project.max_participants - project.current_participants) > 1 ? $t('messages.spots_free') : $t('messages.spots_free_one') }}
          </div>
          <div v-if="isProjectFull(project) && !isProjectDelivered(project) && !isProjectUserDelivered(project)">
            {{ $t('messages.sorry_full') }}
          </div>
          <div v-if="!isProjectSet(project) && isProjectOpen(project) && !isProjectDelivered(project) && !isProjectUserDelivered(project)">
            {{ project.min_participants - project.current_participants }} {{ (project.min_participants - project.current_participants) > 1 ? $t('messages.participants_needed') : $t('messages.participants_needed_one') }}
          </div>
          <div v-if="isProjectClosed(project) && !isProjectDelivered(project) && !isProjectUserDelivered(project)">
            {{ $t('messages.closed') }}
          </div>
          <div v-if="isProjectDelivered(project) && !isProjectUserDelivered(project)">
            {{ $t('messages.delivered') }}
          </div>
          <div v-if="isProjectUserDelivered(project)">
            {{ $t('messages.user_delivered') }}
          </div>
        </div>
        <div v-if="!isProjectApproved(project)">
          {{ $t('messages.to_approve') }}
        </div>
        <div class="promotion" v-if="project.discount > 0">
          <div class="promotion__wrapper">
            <p>{{ project.discount }}%</p>
            <p :class="localeClass" >{{ $t('messages.discount').toLowerCase() }}</p>
          </div>
        </div>
      </div>
    </a>
    <a class="box-nav" :href="'/user/' + this.slugText(project.user_id + '-' + project.user_full_name)">
      <div class="card__started__by">
        <p>{{ $t('messages.started_by') }} <b-button v-if="project.store" v-b-tooltip.hover :title="$t('messages.store_account_info')" class="ml-1 mr-1 noPadding noBackground secondary"><font-awesome-icon icon="store-alt"></font-awesome-icon></b-button> <b-link :to="{ path: '/user/' + slugText(project.user_id + '-' + project.user_full_name) }">{{ project.user_full_name }}</b-link></p>
      </div>
    </a>
    <a class="box-nav h-100" :href="'/project/' + this.slugText(project.id + '/' + project.name)">
      <h1 class="card__title">{{ project.name }}</h1>
    </a>
    <a class="box-nav" :href="'/project/' + this.slugText(project.id + '/' + project.name)">
      <div class="card__bottom" :class="{ 'card__bottom__fix': showPayments }">
        <div class="card__box-icons">
          <div class="box-icon green strong">
            <div class="box-icon-wrapper">
              <font-awesome-icon class="green" icon="calendar-alt"></font-awesome-icon>
            </div>
            <span>{{ translateMonth(project.expected_date) }}</span>
          </div>
        </div>
        <div class="card__price" v-if="project.new_total_price">
          <p>
            <span class="green strong">€ {{ project.new_total_price | round(2) }}</span>
          </p>
        </div>
      </div>
    </a>
    <a class="box-nav" :href="'/project/' + this.slugText(project.id + '/' + project.name)">
      <div class="card__payment" :class="getPaymentBackground(project)" v-if="showPayments">
        <div class="d-flex">
          <div class="card__payment__part">
            <div>{{ $t('messages.amount') }}:</div>
            <div>€ {{ project.amount | round(2) }}</div>
          </div>
          <div class="card__payment__part">
            <div>{{ $t('messages.paid') }}:</div>
            <div>€ {{ project.paid | round(2) }}</div>
          </div>
          <div class="card__payment__part">
            <div>{{ $t('messages.difference') }}:</div>
            <div>€ {{ project.difference | round(2) }}</div>
          </div>
        </div>
      </div>
    </a>
    <div class="clear"></div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueMasonry from 'vue-masonry-css'

import { defaultMixin } from '../../mixin/default'
import { projectMixin } from '../../mixin/project'

import { projectService } from '../../services/project.service'

Vue.use(VueMasonry)

export default {
  name: 'projecttile',
  mixins: [ defaultMixin, projectMixin ],
  data: function () {
    return {
      userLoading: false
    }
  },
  props: {
    project: {
      type: Object,
      required: true
    },
    showPayments: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    localeClass: function () {
      return this.$i18n.locale
    }
  },
  methods: {
    getPaymentBackground: function (project) {
      if (project.difference >= 0) {
        return 'bg-success text-white font-weight-bold'
      } else if (project.difference < 0 && project.difference !== (project.amount * -1)) {
        return 'bg-warning text-white font-weight-bold'
      } else {
        return 'bg-danger text-white font-weight-bold'
      }
    },
    userDelivered: function () {
      this.userLoading = true
      if (parseInt(this.$store.state.userId) === parseInt(this.project.user_id)) {
        projectService.delivered(this.project.id)
          .then(() => {
            this.project.delivered = true
            projectService.userDeliveredSelf(this.project.id)
              .then(() => {
                this.$store.commit('success', 'success.user_delivered_self')
                this.project.user_delivered = true
                this.userLoading = false
              }, () => {
                this.userLoading = false
                this.$store.commit('error', 'errors.user_delivered_self')
              })
          }, () => {
            this.userLoading = false
            this.$store.commit('error', 'errors.user_delivered_self')
          })
      } else {
        projectService.userDeliveredSelf(this.project.id)
          .then(() => {
            this.$store.commit('success', 'success.user_delivered_self')
            this.project.user_delivered = true
            this.userLoading = false
          }, () => {
            this.userLoading = false
            this.$store.commit('error', 'errors.user_delivered_self')
          })
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .card {
    cursor: pointer;
  }

  .box-nav, .box-nav:hover {
    color: inherit;
    text-decoration: none;
    border: none;
  }

  .card__delivered {
    position: absolute;
    left: 10px;
    top: 10px;
  }

  .card__progress {
    /* IE 8 */
    -ms-filter: "progid:DXImageTransform.Microsoft.Alpha(Opacity=50)";
    /* IE 5-7 */
    filter: alpha(opacity=50);
    /* Netscape */
    -moz-opacity: 0.5;
    /* Safari 1.x */
    -khtml-opacity: 0.5;
    /* Good browsers */
    opacity: 0.5;
  }

  .card__title {
    height: 100%;

  }

  .card__started__by {
    padding: 10px 20px;
    border-bottom: 1px #D3D3D3 solid;
  }

  .card__started__by p {
    min-height: 25px;
  }

  @media screen and (min-width: 701px) {
    .card {
      cursor: pointer;
      height: 500px;
    }

    .card__payments {
      height: 500px !important;
    }
  }

  @media screen and (min-width: 1001px) {
    .card {
      cursor: pointer;
      height: 425px;
    }

    .card__payments {
      height: 500px;
    }
  }

  .card__info, .max-height-40 {
    height: 40px;
  }

  .card__payment {
    border-top: 1px #D3D3D3 solid;
    width: 100%;
    height: 63px;
  }

  .card__payment__part {
    -webkit-flex-grow: 1; /* Safari */
    flex-grow: 1;
    text-align: center;
    padding: 10px;
  }

  .promotion {
    float: right;
    margin: -45px 0px 0px;
    width: 60px;
    height: 60px;
    background: url('../../assets/discount.png');
    background-size: 100%;
    color: #FFFFFF;
    font-weight: bold;
    text-transform: uppercase;

    -webkit-transform: rotate(-20deg);
    -moz-transform: rotate(-20deg);
    -ms-transform: rotate(-20deg);
    -o-transform: rotate(-20deg);
    filter: progid:DXImageTransform.Microsoft.BasicImage(rotation=2);
  }

  .promotion .promotion__wrapper {
    margin-top: -3px;
  }

  .promotion .promotion__wrapper p {
    text-align: center;
  }

  .promotion .promotion__wrapper p.nl {
    font-size: 7.9px;
  }

  .promotion .promotion__wrapper p.en {
    font-size: 7.0px;
  }

  .promotion .promotion__wrapper p:first-child {
    margin-top: 15px;
    margin-bottom: -5px;
    font-size: 18px;
  }

  .card__bottom {
    display: flex;
    justify-content: space-between;
    padding: 0px;
  }

  @media screen and (min-width: 901px) {
    .card__bottom {
      position: absolute;
      bottom: 0px;
      width: 100%;
    }

    .card__bottom__fix {
      bottom: 62px;
    }
  }

  .img-fluid {
    width: 100%;
  }

  .clear {
    clear: both;
  }
</style>



// WEBPACK FOOTER //
// src/components/project/ProjectTile.vue